import { Pose as P } from '@digital-bridge/artisan-arya';
import { Orientation3D, Position3D, Vector3D } from '@digital-bridge/fine';
import { Newtype, iso } from 'newtype-ts';

export interface Pose extends Newtype<{ readonly Pose: unique symbol }, P> {}

const _ = iso<Pose>();

export const construct = _.wrap;
export const constructFromRaw = (
    position: { x: number; y: number; z: number },
    orientation: { w: number; x: number; y: number; z: number },
) =>
    construct({
        position: new Position3D(position.x, position.y, position.z),
        orientation: new Orientation3D(
            orientation.w,
            new Vector3D([orientation.x, orientation.y, orientation.z]),
        ),
    });

export const position = (p: Pose) => _.unwrap(p).position;
export const orientation = (p: Pose) => _.unwrap(p).orientation;

export const identity = () =>
    construct({ position: new Position3D(0, 0, 0), orientation: Orientation3D.identity() });

function serialisePosition(p: Pose) {
    const [x, y, z] = position(p).value();
    return { x, y, z };
}

function serialiseOrientation(p: Pose) {
    const [w, x, y, z] = orientation(p).quaternion();
    return { w, x, y, z };
}

export function serialise(p: Pose): {
    position: { x: number; y: number; z: number };
    orientation: { w: number; x: number; y: number; z: number };
} {
    return { position: serialisePosition(p), orientation: serialiseOrientation(p) };
}

export function fromAryaTransform({
    rotation,
    position,
}: {
    rotation: Orientation3D;
    position: Position3D;
}): Pose {
    return _.wrap({ position, orientation: rotation });
}

export function toAryaTransform(p: Pose): { position: Position3D; rotation: Orientation3D } {
    const { position, orientation } = _.unwrap(p);
    return { position, rotation: orientation };
}
